import React from 'react';
import { Link } from 'react-router-dom';
import SectionTitle from '@/components/SectionTitle';
import Seo from '@/seo/Seo';
import { PAGE_SEO } from '@/seo/seoConfig';
import { breadcrumbSchema } from '@/seo/schemas';
import { Cross, BookOpen, Sparkles, Heart, HandHeart, Sun, Users, Globe, Quote, Mail, Phone, MessageCircle } from 'lucide-react';
import { CHURCH } from '@/api/client';

const BELIEFS = [
  { icon: Cross, title: 'Jesus Christ is Lord', text: 'We confess Jesus Christ as the only begotten Son of God, crucified, risen and reigning Lord and Saviour of all.' },
  { icon: BookOpen, title: 'The Bible is the Word of God', text: 'The Holy Scriptures are God-breathed, infallible, and the supreme authority for faith, doctrine and life.' },
  { icon: Heart, title: 'Salvation by Grace through Faith', text: 'Salvation is the free gift of God received through faith in the finished work of Jesus on the cross.' },
  { icon: Sparkles, title: 'The Holy Spirit Empowers Believers', text: 'We embrace the baptism, gifts, fruit and ongoing ministry of the Holy Spirit in every disciple of Christ.' },
  { icon: HandHeart, title: 'Prayer Brings Transformation', text: 'Through fervent, believing prayer, lives are changed, families restored and communities touched by God.' },
  { icon: Sun, title: 'Healing & Testimony Glorify God', text: 'Jesus still heals and delivers today. Every testimony magnifies the goodness and power of our God.' },
  { icon: Globe, title: 'Called to Evangelism & Service', text: 'The church exists to make Jesus known — in word, deed, love and the demonstration of the Spirit.' },
];

const VALUES = [
  { title: 'Worship', text: 'Spirit-led, heartfelt, Christ-exalting worship.' },
  { title: 'Word', text: 'Devotion to the apostles\u2019 teaching and the whole counsel of God.' },
  { title: 'Prayer', text: 'A house of prayer for all nations.' },
  { title: 'Family', text: 'Every age belongs; every gift matters.' },
  { title: 'Holiness', text: 'Walking worthy of our calling in Christ.' },
  { title: 'Mission', text: 'Reaching Ireland and the nations with the gospel.' },
];

export default function About() {
  return (
    <div>
      <Seo
        title={PAGE_SEO.about.title}
        titleExact
        description={PAGE_SEO.about.description}
        keywords={PAGE_SEO.about.keywords}
        path="/about"
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])]}
      />
      <section className="gradient-purple text-white py-12 sm:py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-moj-gold text-sm font-bold uppercase tracking-[0.3em] mb-3">About our church</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">Ministry of Jesus</h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto">
            {CHURCH.tagline}. A Pentecostal church family in Ireland, centred on Jesus Christ, the Word of God, prayer, worship, and the power of the Holy Spirit.
          </p>
        </div>
      </section>

      <section className="py-20 bg-moj-cream-soft">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-10">
          <div className="card-moj p-8"><h2 className="font-serif text-2xl mb-3">Our Vision</h2>
            <p className="text-moj-charcoal/80">To raise a Spirit-empowered generation in Ireland that knows Christ deeply and makes Him known boldly — in homes, streets, schools, and nations.</p>
          </div>
          <div className="card-moj p-8"><h2 className="font-serif text-2xl mb-3">Our Mission</h2>
            <p className="text-moj-charcoal/80">To love God, love people, and labour in the harvest — making disciples through worship, the Word, prayer, the Spirit, and acts of mercy.</p>
          </div>
          <div className="card-moj p-8"><h2 className="font-serif text-2xl mb-3">Our Scripture</h2>
            <p className="font-serif text-xl italic text-moj-purple-deep mb-2">“Not by might, nor by power, but by my Spirit, says the LORD of hosts.”</p>
            <p className="text-moj-gold-deep font-semibold">Zechariah 4:6</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-moj-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="What we believe" title="Pentecostal Belief Statement" center
            subtitle="We hold to the historic Christian faith, lived in the power of the Holy Spirit."/>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BELIEFS.map((b) => (
              <div key={b.title} className="card-moj p-6" data-testid={`belief-${b.title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                <b.icon size={32} className="text-moj-gold-deep mb-3"/>
                <h3 className="font-serif text-lg mb-2 text-moj-purple-deep">{b.title}</h3>
                <p className="text-sm text-moj-charcoal/80">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 gradient-soft-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="Senior Pastor & Founder" title="Meet Pastor Jayaseelan Vincent" center
            subtitle="A faithful servant of Christ, shepherding Ministry of Jesus with the Word, prayer and the power of the Holy Spirit." />
          <div className="grid lg:grid-cols-[420px_1fr] gap-10 items-center">
            <div className="relative mx-auto lg:mx-0">
              <div className="absolute -inset-4 rounded-3xl gradient-purple-gold opacity-30 blur-2xl"></div>
              <div className="relative card-moj overflow-hidden border-4 border-moj-gold/40">
                <img
                  src="https://customer-assets.emergentagent.com/job_pentecost-portal/artifacts/3nihx2pq_Pastor-Pic_1.jpg"
                  alt="Pastor Jayaseelan Vincent, Senior Pastor of Ministry of Jesus"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 gradient-purple text-white p-5">
                  <div className="text-moj-gold text-[10px] uppercase tracking-[0.3em] font-bold">Senior Pastor</div>
                  <div className="font-serif text-2xl mt-1">Ps. Jayaseelan Vincent</div>
                </div>
              </div>
            </div>

            <div>
              <Quote size={36} className="text-moj-gold-deep mb-3"/>
              <p className="text-lg sm:text-xl font-serif italic text-moj-purple-deep leading-relaxed mb-5">
                "Our calling is simple — to know Christ and make Him known in Ireland. In every prayer, every sermon, every act of service, we want hearts to encounter the living God and lives to be transformed by His Spirit."
              </p>
              <p className="text-sm text-moj-charcoal/80 mb-5">— Pastor Jayaseelan Vincent</p>

              <div className="card-moj p-6 mb-5">
                <h3 className="font-serif text-xl text-moj-purple-deep mb-3">A heart for Ireland</h3>
                <p className="text-moj-charcoal/85 leading-relaxed">
                  Pastor Jayaseelan Vincent serves as the Senior Pastor and Founder of Ministry of Jesus, Ireland. With a deep love for the Word of God, a passion for prayer, and an anointing for healing and deliverance ministry, he has dedicated his life to building a Spirit-filled church family that exalts Jesus Christ.
                </p>
                <p className="text-moj-charcoal/85 leading-relaxed mt-3">
                  Under his pastoral leadership, the church gathers weekly for worship, runs early morning prayer, midweek services, Sunday School, evangelism on the streets of Dublin, youth and children's programs, and a global online prayer ministry that reaches believers across Ireland and beyond.
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div className="card-moj p-4 text-center">
                  <BookOpen size={22} className="text-moj-gold-deep mx-auto mb-1"/>
                  <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold">Ministry</div>
                  <div className="text-sm font-serif text-moj-purple-deep">Word & Spirit</div>
                </div>
                <div className="card-moj p-4 text-center">
                  <HandHeart size={22} className="text-moj-gold-deep mx-auto mb-1"/>
                  <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold">Calling</div>
                  <div className="text-sm font-serif text-moj-purple-deep">Prayer & Healing</div>
                </div>
                <div className="card-moj p-4 text-center">
                  <Globe size={22} className="text-moj-gold-deep mx-auto mb-1"/>
                  <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold">Vision</div>
                  <div className="text-sm font-serif text-moj-purple-deep">Reach Ireland</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/contact" className="btn-primary text-sm"><Mail size={14}/> Contact Pastor</Link>
                <a
                  href={`https://wa.me/${CHURCH.phoneIntl.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Grace and peace! I would like to chat with Pastor Jayaseelan Vincent about ')}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full font-semibold bg-[#25D366] text-white hover:bg-[#1FB855] transition shadow-sm"
                  data-testid="pastor-whatsapp-btn"
                  aria-label="Chat with Pastor Jayaseelan Vincent on WhatsApp"
                >
                  <MessageCircle size={14}/> Chat on WhatsApp
                </a>
                <Link to="/morning-prayer" className="btn-outline text-sm"><Sun size={14}/> Join Morning Prayer</Link>
                <a href={`tel:${CHURCH.phoneIntl.replace(/\s/g,'')}`} className="btn-outline text-sm"><Phone size={14}/> {CHURCH.phoneIntl}</a>
              </div>
            </div>
          </div>

          <div className="mt-16">
            <SectionTitle kicker="Our Core Values" title="What shapes our community" center />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {VALUES.map((v) => (
                <div key={v.title} className="card-moj p-5">
                  <h4 className="font-serif text-lg text-moj-purple-deep">{v.title}</h4>
                  <p className="text-sm text-moj-charcoal/80 mt-1">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 gradient-purple text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif text-4xl text-white mb-4">Come and experience God with us</h2>
          <p className="text-white/90 text-lg mb-8">Sunday services begin at 10:30. There’s a seat saved for you and your family.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gold">Get in touch</Link>
            <Link to="/services" className="btn-outline !text-white !border-moj-gold hover:!bg-moj-gold hover:!text-moj-purple-deep">Explore ministries</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
